// This component deliberately mirrors the admin universal-filter fork.
// fallow-ignore-file code-duplication
import { cn } from 'cn';
import {
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';

import { getFormattedNumber } from '@vestigas/utils';

import { Badge, Icon, IconButton } from '@vestigas/ui';
import { SearchInput } from '@vestigas/ui/molecules';
import { DateRangeSelect } from '~/ui/molecules/DateInput';

import { getOwnedFieldKeys } from '../drawerFieldKeys';
import { useUniversalFiltersSelector } from '../hooks/useUniversalFiltersSelector';
import { useUniversalFiltersStore } from '../hooks/useUniversalFiltersStore';
import { getDraftSearch } from '../store';
import { useUniversalFiltersContext } from '../UniversalFiltersProvider';

import { usePrimaryDateFilter } from './usePrimaryDateFilter';

type PrimaryFiltersRowProps = PropsWithChildren<{
  readonly debounceTime?: number;
  readonly drawerButtonLabel?: string;
  readonly sortComponent?: ReactNode;
}> &
  ComponentStyling;

/**
 * Renders the configured always-visible filter controls.
 *
 * Date, search, and drawer trigger controls appear only when configured.
 * Page-owned children and a trailing sort component may be appended.
 */
// The RAF-based focus is required because native autoFocus fires too early here.
// fallow-ignore-next-line complexity
export function PrimaryFiltersRow({
  children,
  className,
  debounceTime = 300,
  drawerButtonLabel,
  sortComponent,
  style,
}: PrimaryFiltersRowProps) {
  const { t } = useTranslation('universalFilters');

  const { config, openDrawer } = useUniversalFiltersContext();
  const store = useUniversalFiltersStore();
  const draft = useUniversalFiltersSelector((context) => context.draft);
  const search = useUniversalFiltersSelector((context) =>
    getDraftSearch(context.draft),
  );

  const searchInputRef = useRef<HTMLInputElement>(null);

  const hasDrawerFields = Boolean(config.drawerFields?.length);
  // A drawer row without a value is not an active filter yet.
  const activeDrawerFilterCount = (config.drawerFields ?? []).filter(
    (fieldConfig) =>
      getOwnedFieldKeys(fieldConfig).some((key) =>
        draft.some((clause) => clause.field === key && clause.value != null),
      ),
  ).length;
  const hasActiveDrawerFilters = activeDrawerFilterCount > 0;
  const drawerButtonText = drawerButtonLabel ?? t(($) => $.drawerButton.text);
  const drawerButtonAriaLabel = hasActiveDrawerFilters
    ? t(($) => $.drawerButton.activeAriaLabel, {
        label: drawerButtonText,
      })
    : drawerButtonText;
  const dateField = config.dateField;
  const {
    dateRange,
    handleDateRangeChange,
    handlePredefinedDateRangeChange,
    predefinedDateRange,
  } = usePrimaryDateFilter(dateField);

  function handleSearchChange(nextSearch: string) {
    store.trigger.setSearch({ search: nextSearch });
  }

  useEffect(() => {
    // Use requestAnimationFrame to wait for the DOM to be fully rendered
    const rafId = requestAnimationFrame(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div
      className={cn('flex flex-wrap items-center gap-2', className)}
      style={style}
    >
      {dateField ? (
        <div>
          <span className="sr-only">{dateField.label}</span>
          <DateRangeSelect
            dateRange={dateRange}
            isIndividualDateRange={
              !predefinedDateRange && dateRange.every(Boolean)
            }
            predefinedDateRange={predefinedDateRange ?? 'individual'}
            testId="universal-filters-date-range"
            onDateRangeChange={handleDateRangeChange}
            onPredefinedDateRangeChange={handlePredefinedDateRangeChange}
          />
        </div>
      ) : null}

      {config.search ? (
        <div className="min-w-64 flex-1 sm:max-w-96">
          <SearchInput
            ref={searchInputRef}
            debounceTime={debounceTime}
            placeholder={
              config.search.placeholder ?? t(($) => $.search.placeholder)
            }
            testId="universal-filters-search"
            value={search}
            onChange={handleSearchChange}
            onFocus={() => {}}
          />
        </div>
      ) : null}

      {hasDrawerFields ? (
        <IconButton
          aria-label={drawerButtonAriaLabel}
          type="button"
          variant="surface"
          onClick={openDrawer}
        >
          <Icon metaphor="filter" />
          {hasActiveDrawerFilters ? (
            <Badge
              className="-top-2 -right-1.5 z-10 h-5 min-w-5 bg-tiga-action-default leading-5"
              text={getFormattedNumber({ value: activeDrawerFilterCount })}
            />
          ) : null}
        </IconButton>
      ) : null}

      {/* This slot must not clip: an overflow value other than visible cuts off
          the shadow of the controls it holds, which then look flat next to the
          same control rendered outside the slot. */}
      {children ? (
        <div className="flex min-w-0 flex-1 justify-end gap-x-4 gap-y-2">
          {children}
        </div>
      ) : null}

      {sortComponent ? (
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {sortComponent}
        </div>
      ) : null}
    </div>
  );
}
